import { formatBsDate } from '@/lib/bsDate'

/**
 * The market mood: mm-be's Fear & Greed composite, one reading everywhere it is drawn (owner
 * ruling 1, 2026-11-01), `/market/summary/`'s `fear_greed` for today and `/market/fear-greed/` for
 * the days behind it.
 *
 * It blends nine signals (breadth, momentum, turnover and the rest), each ranked against NEPSE's
 * own last 500 sessions, then ranks the blend again. So the score is a percentile: 50 is an
 * ordinary day for NEPSE, not a halfway point in a range.
 *
 * mm-be names the band (`label`: FEAR · CAUTIOUS · NEUTRAL · WARM · GREED) and cuts the scale at
 * 25 / 45 / 60 / 80 (`services/market/fear_greed_v3.py` `BANDS`). The bands are not equal fifths,
 * so the cuts below are what the gauges draw their bands from.
 */

export type MoodBandKey = 'fear' | 'cautious' | 'neutral' | 'warm' | 'greed'

export interface MoodBand {
  key: MoodBandKey
  name: string
  /** Inclusive lower bound, exclusive upper (100 is greed's). */
  from: number
  to: number
  /** The reading in a few words, revealed on hover. */
  line: string
  /** What the band says about the market, one line. */
  says: string
}

export const MOOD_BANDS: readonly MoodBand[] = [
  {
    key: 'fear',
    name: 'Fear',
    from: 0,
    to: 25,
    line: 'Weaker than most recent days',
    says: 'Weaker than most recent days. Sellers have been in charge.',
  },
  {
    key: 'cautious',
    name: 'Cautious',
    from: 25,
    to: 45,
    line: 'Softer than a typical day',
    says: 'Softer than a typical day. More selling than buying lately.',
  },
  {
    key: 'neutral',
    name: 'Neutral',
    from: 45,
    to: 60,
    line: 'A typical day for NEPSE',
    says: 'A typical day for NEPSE. Neither side is in charge.',
  },
  {
    key: 'warm',
    name: 'Warm',
    from: 60,
    to: 80,
    line: 'Stronger than a typical day',
    says: 'Stronger than a typical day. More buying than selling lately.',
  },
  {
    key: 'greed',
    name: 'Greed',
    from: 80,
    to: 100,
    line: 'Stronger than most recent days',
    says: 'Stronger than most recent days. Buyers have been in charge.',
  },
]

/** The scale's inner cut points, for a gauge to size its bands by. */
export const MOOD_CUTS: readonly number[] = MOOD_BANDS.slice(1).map((band) => band.from)

/** At or above this the greed mascot turns to its extreme pose. The band is still Greed. */
export const EXTREME_GREED_FROM = 95

const GREED = MOOD_BANDS[MOOD_BANDS.length - 1] as MoodBand

/** A band by its key. */
export const moodBandByKey = (key: MoodBandKey): MoodBand =>
  MOOD_BANDS.find((band) => band.key === key) ?? GREED

/**
 * The band mm-be named, read off its `label` whatever the case. A label this build does not know
 * is an upstream fact, not a shape error, so the score's own cut stands in for it.
 */
export const moodBand = (label: string, score: number): MoodBand => {
  const word = label.trim().toLowerCase()
  return (
    MOOD_BANDS.find((band) => band.key === word) ??
    MOOD_BANDS.find((band) => score < band.to) ??
    GREED
  )
}

/** Whether a reading's date is today on NEPSE's calendar, the Kathmandu day. */
export const isMoodToday = (date: string): boolean =>
  formatBsDate(date) === formatBsDate(new Date().toISOString())
